import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/breadcrumbs";
import type { Breadcrumb as BreadcrumbItem } from "@/lib/seo";
import { DEFAULT_AUTHOR } from "@/content/authors";
import { formatDateLong } from "@/lib/format-date";

type HeroAuthor = { name: string; credentials: string; slug?: string };

type PageHeroProps = {
  eyebrow: string;
  title: ReactNode;
  subtitle?: ReactNode;
  intro?: ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  /** Set false where the route's head() already emits BreadcrumbList JSON-LD. */
  breadcrumbSchema?: boolean;
  lastReviewed?: string;
  author?: HeroAuthor;
  aside?: ReactNode;
  actions?: ReactNode;
};

export function PageHero({
  eyebrow,
  title,
  subtitle,
  intro,
  breadcrumbs,
  breadcrumbSchema = true,
  lastReviewed,
  author,
  aside,
  actions,
}: PageHeroProps) {
  const reviewer = lastReviewed ? author ?? DEFAULT_AUTHOR : null;
  return (
    <section className="relative overflow-hidden border-y border-border/70 bg-secondary/40 px-6 py-12 lg:px-8 lg:py-16">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 right-0 hidden w-1/3 bg-gradient-to-l from-background/60 to-transparent lg:block"
      />
      <div className="relative mx-auto max-w-7xl">
        {/* Top meta row: breadcrumbs + reviewer */}
        <div className="mb-10 flex flex-col gap-3 border-b border-border/60 pb-4 md:flex-row md:items-center md:justify-between">
          {breadcrumbs && breadcrumbs.length > 0 ? (
            <Breadcrumbs
              items={breadcrumbs}
              emitSchema={breadcrumbSchema}
              className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground"
            />
          ) : (
            <span />
          )}
          {reviewer && lastReviewed && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span aria-hidden="true" className="inline-block size-1.5 rounded-full bg-accent" />
              <span>
                Reviewed by{" "}
                {reviewer.slug ? (
                  <Link
                    to="/about-employerhq"
                    hash={`author-${reviewer.slug}`}
                    className="font-semibold text-brand underline decoration-accent/50 underline-offset-2 hover:text-accent"
                  >
                    {reviewer.name}
                  </Link>
                ) : (
                  <span className="font-semibold text-brand">{reviewer.name}</span>
                )}
                <span className="hidden sm:inline"> · {reviewer.credentials}</span>
                <span> · Updated </span>
                <time dateTime={lastReviewed}>{formatDateLong(lastReviewed)}</time>
              </span>
            </div>
          )}
        </div>

        <div
          className={
            aside
              ? "grid grid-cols-1 items-start gap-12 lg:grid-cols-12"
              : ""
          }
        >
          <div className={aside ? "lg:col-span-7" : "max-w-4xl"}>
            <div className="mb-6 inline-flex items-center gap-3">
              <span aria-hidden="true" className="h-px w-8 bg-accent" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent">
                {eyebrow}
              </span>
            </div>
            <h1 className="font-serif text-4xl leading-[1.1] text-brand sm:text-5xl lg:text-6xl">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-6 max-w-2xl text-sm font-semibold uppercase tracking-wider text-accent">
                {subtitle}
              </p>
            )}
            {intro && (
              <div className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
                {intro}
              </div>
            )}
            {actions && <div className="mt-8 flex flex-wrap items-center gap-4">{actions}</div>}
          </div>
          {aside && <div className="lg:col-span-5">{aside}</div>}
        </div>
      </div>
    </section>
  );
}
